import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fromLocalInput, fmt } from "@/lib/api";
import { Modal } from "@/components/ui-bits";
import { SlotPicker } from "@/components/SlotPicker";
import { SessionNotesModal } from "@/components/SessionNotes";
import { CoachSelect } from "@/components/CoachSelect";

const TOAST = { confirm: "Session confirmed", request_change: "Change request sent", complete: "Marked done", cancel: "Session cancelled", no_show: "Marked no-show", reschedule: "Slot confirmed", reschedule_self: "Moved — your coach has been notified", assign_coach: "Coach assigned" };

export const SessionActions = ({ session, admin, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // "reschedule" | "change"
  const [slot, setSlot] = useState(null);
  const [manual, setManual] = useState("");
  const [note, setNote] = useState("");
  const [askNote, setAskNote] = useState(false);
  const { id, status } = session;

  const act = async (action, extra = {}) => {
    setBusy(true);
    try {
      await api.patch(`/sessions/${id}`, { action, ...extra });
      toast.success(TOAST[action] || "Updated");
      setModal(null); setSlot(null); setManual(""); setAskNote(false);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const modals = (
    <>
      {modal === "notes" && <SessionNotesModal session={session} onClose={() => setModal(null)} onSaved={() => { setModal(null); onDone?.(); }} />}
      {modal === "reschedule" && (
        <Modal title="RE-SLOT SESSION" sub={`Currently ${fmt(session.startAt)}. Pick a free slot below, or type a time to override.`} onClose={() => setModal(null)} testId="reschedule-modal">
          {session.changeNote && <div className="msg"><div className="who"><span>Client asked for</span></div><div className="txt">{session.changeNote}</div></div>}
          <SlotPicker excludeSessionId={id} value={slot} onChange={(s) => { setSlot(s); setManual(""); }} />
          <label>Or set any time manually (gym time)</label>
          <input type="datetime-local" value={manual} onChange={(e) => { setManual(e.target.value); setSlot(null); }} data-testid="reschedule-datetime-input" />
          <div className="mfoot">
            <button className="btn g" onClick={() => setModal(null)} data-testid="reschedule-cancel-button">Cancel</button>
            {status === "CHANGE_REQUESTED" && <button className="btn g" disabled={busy} onClick={() => act("confirm")} data-testid="reschedule-keep-button">Keep current slot</button>}
            <button className="btn" disabled={busy || (!slot && !manual)} onClick={() => act("reschedule", { startAt: slot || fromLocalInput(manual) })} data-testid="reschedule-save-button">
              {slot || manual ? `Confirm ${fmt(slot || fromLocalInput(manual))}` : "Pick a slot"}
            </button>
          </div>
        </Modal>
      )}
      {modal === "change" && (
        <Modal title="MOVE YOUR SESSION" sub="Only slots your coach actually has free are shown. Pick one and you're locked in — no waiting." onClose={() => setModal(null)} testId="change-request-modal">
          {!askNote ? (
            <>
              <SlotPicker excludeSessionId={id} value={slot} onChange={setSlot} />
              <div className="mfoot" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
                <button className="btn g sm" onClick={() => setAskNote(true)} data-testid="change-ask-coach-button">None of these work? Ask coach</button>
                <span style={{ display: "flex", gap: 8 }}>
                  <button className="btn g" onClick={() => setModal(null)} data-testid="change-cancel-button">Back</button>
                  <button className="btn" disabled={busy || !slot} onClick={() => act("reschedule_self", { startAt: slot })} data-testid="change-submit-button">
                    {slot ? `Move to ${fmt(slot)}` : "Pick a slot"}
                  </button>
                </span>
              </div>
            </>
          ) : (
            <>
              <label>Tell your coach what would suit you</label>
              <input placeholder="e.g. Sat 7am or Thu evening" value={note} onChange={(e) => setNote(e.target.value)} data-testid="change-note-input" />
              <div className="mfoot">
                <button className="btn g" onClick={() => setAskNote(false)} data-testid="change-note-back-button">Back to slots</button>
                <button className="btn" disabled={busy || !note.trim()} onClick={() => act("request_change", { note })} data-testid="change-note-submit-button">Send to coach</button>
              </div>
            </>
          )}
        </Modal>
      )}
    </>
  );

  if (admin) {
    return (
      <span className="actions">
        <CoachSelect compact value={session.trainerId} onChange={(v) => act("assign_coach", { trainerId: v })} testId={`assign-coach-${id}`} />
        <button className="btn sm g" disabled={busy} onClick={() => setModal("notes")} data-testid={`notes-${id}`}>Notes</button>
        {status === "CHANGE_REQUESTED" && <>
          <button className="btn sm" disabled={busy} onClick={() => setModal("reschedule")} data-testid={`accept-change-${id}`}>Re-slot</button>
          <button className="btn sm g" disabled={busy} onClick={() => act("confirm")} data-testid={`keep-slot-${id}`}>Keep slot</button></>}
        {status === "AWAITING_CONFIRMATION" && <button className="btn sm" disabled={busy} onClick={() => act("confirm")} data-testid={`admin-confirm-${id}`}>Confirm</button>}
        {(status === "CONFIRMED" || status === "AWAITING_CONFIRMATION") && <>
          <button className="btn sm g" disabled={busy} onClick={() => act("complete")} data-testid={`complete-${id}`}>Done</button>
          <button className="btn sm g" disabled={busy} onClick={() => setModal("reschedule")} data-testid={`move-${id}`}>Move</button>
          <button className="btn sm g" disabled={busy} onClick={() => act("no_show")} data-testid={`no-show-${id}`}>No-show</button>
          <button className="btn sm danger" disabled={busy} onClick={() => act("cancel")} data-testid={`cancel-${id}`}>Cancel</button></>}
        {modals}
      </span>
    );
  }
  const open = ["AWAITING_CONFIRMATION", "CONFIRMED", "CHANGE_REQUESTED", "SCHEDULED"].includes(status);
  if (!open) return null;
  return (
    <span className="actions">
      {status !== "CONFIRMED" && <button className="btn sm" disabled={busy} onClick={() => act("confirm")} data-testid={`client-confirm-${id}`}>Confirm</button>}
      <button className="btn sm g" disabled={busy} onClick={() => setModal("change")} data-testid={`client-change-${id}`}>Change time</button>
      {modals}
    </span>
  );
};
